'use client';
import { useCallback } from 'react';
import { useRouter } from 'next/navigation';

/**
 * 리다이렉트 URL을 관리하는 훅
 * 
 * 이 훅은 리다이렉트 URL을 저장, 조회, 삭제하고 해당 URL로 이동하는 기능을 제공합니다.
 * 주로 인증 흐름에서 사용자가 원래 접근하려던 페이지로 돌아가기 위해 사용됩니다.
 * 
 * @returns 리다이렉트 URL 관리 함수들
 * 
 * @example
 * ```tsx
 * const { set, clear, push, replace } = useRedirect();
 * 
 * // 현재 URL 저장
 * set(window.location.pathname);
 * 
 * // 저장된 URL로 이동 (없으면 홈으로)
 * push('/');
 * 
 * // 저장된 URL로 이동 (replace 사용, 없으면 홈으로)
 * replace('/');
 * 
 * // 저장된 URL 삭제
 * clear();
 * ```
 */
const useRedirect = () => {
  const router = useRouter();

  /**
   * 리다이렉트 URL을 저장합니다.
   * 
   * @param url 저장할 URL
   */
  const set = useCallback((url: string) => {
    localStorage.setItem('redirectUrl', url);
  }, []);

  /**
   * 저장된 리다이렉트 URL을 가져옵니다.
   * 
   * @returns 저장된 URL 또는 null
   */
  const get = useCallback((): string | null => {
    return localStorage.getItem('redirectUrl');
  }, []);

  /**
   * 저장된 리다이렉트 URL을 삭제합니다.
   */
  const clear = useCallback(() => {
    localStorage.removeItem('redirectUrl');
  }, []);

  /**
   * 저장된 리다이렉트 URL로 이동합니다.
   * 저장된 URL이 없으면 기본 URL로 이동합니다.
   * 
   * @param defaultUrl 기본 URL (저장된 URL이 없을 때 사용)
   */
  const push = useCallback((defaultUrl: string = '/') => {
    const redirectUrl = get();
    if (redirectUrl) {
      clear();
      router.push(redirectUrl);
    } else {
      router.push(defaultUrl);
    }
  }, [router, get, clear]);

  /**
   * 저장된 리다이렉트 URL로 이동합니다 (replace 사용).
   * 저장된 URL이 없으면 기본 URL로 이동합니다.
   * 
   * @param defaultUrl 기본 URL (저장된 URL이 없을 때 사용)
   */
  const replace = useCallback((defaultUrl: string = '/') => {
    const redirectUrl = get();
    if (redirectUrl) {
      clear();
      router.replace(redirectUrl);
    } else {
      router.replace(defaultUrl);
    }
  }, [router, get, clear]);

  return {
    set,
    get,
    clear,
    push,
    replace
  };
};

export default useRedirect;